import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useExpress, { expressMutation } from '../../../../../repository/react-query/useExpress';

// Get a single effective line item rate
export const getEffectiveLineItemRate = (effectiveRateId) => {
    const endpoint = '/effective-payment-line-item-rate';
    const params = {
        where: {
            id: effectiveRateId,
        },
    };
    return useExpress({ endpoint, params });
};

export const useEffectiveLineItemRate = ({ effectiveRateId, enabled = true } = {}) => {
    const queryFn = getEffectiveLineItemRate(effectiveRateId);
    const queryKey = ['effective-payment-line-item-rate', effectiveRateId];
    return useQuery({ queryKey, queryFn, enabled, cacheTime: 0 });
};

// Get all effective line item rates for a rate ID
export const getEffectiveLineItemRates = (rateId) => {
    const endpoint = '/effective-payment-line-item-rate';
    const params = {
        where: {
            payment_line_item_rate_id: rateId,
            deleted_at: null,
        },
        populate: {
            processor_ids: ['id', 'license_number'],
            include_producer_ids: ['id', 'license_number'],
            exclude_producer_ids: ['id', 'license_number'],
            route_ids: ['id', 'name'],
            hauler_ids: ['id', 'hauler_number'],
        },
    };
    return useExpress({ endpoint, params });
};

export const useEffectiveLineItemRates = ({ rateId, enabled = true } = {}) => {
    const queryFn = getEffectiveLineItemRates(rateId);
    const queryKey = ['effective-payment-line-item-rates', rateId];
    return useQuery({ queryKey, queryFn, enabled, cacheTime: 0 });
};

// Create a new effective payment line item rate
const createEffectivePaymentLineItemRate = (data) => {
    return expressMutation({ method: 'POST', endpoint: '/effective-payment-line-item-rate', body: data });
};

export const useCreateEffectivePaymentLineItemRate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createEffectivePaymentLineItemRate,
        onSuccess: (_, variables) => {
            const paymentLineItemRateId = variables.payment_line_item_rate_id;
            queryClient.invalidateQueries({ queryKey: ['effective-payment-line-item-rates', paymentLineItemRateId] });
        },
    });
};

// Edit an existing effective payment line item rate
const editEffectivePaymentLineItemRate = ({ rateId, data }) => {
    return expressMutation({ method: 'PUT', endpoint: `/effective-payment-line-item-rate/${rateId}`, body: data });
};

export const useEditEffectivePaymentLineItemRate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: editEffectivePaymentLineItemRate,
        onSuccess: (_, variables) => {
            const paymentLineItemRateId = variables.data.payment_line_item_rate_id;
            const effectiveRateId = variables.rateId;
            // Invalidate and refetch relevant queries
            queryClient.invalidateQueries({ queryKey: ['effective-payment-line-item-rates', paymentLineItemRateId] });
            queryClient.invalidateQueries({ queryKey: ['effective-payment-line-item-rate', effectiveRateId] });
        },
    });
};

// Delete an effective payment line item rate
const deleteEffectivePaymentLineItemRate = ({ rateId }) => {
    return expressMutation({
        endpoint: `/effective-payment-line-item-rate/${rateId}`,
        method: 'DELETE',
    });
};

export const useDeleteEffectivePaymentLineItemRate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteEffectivePaymentLineItemRate,
        onSuccess: () => {
            // We need to invalidate the effective rates list for the parent payment line item rate
            queryClient.invalidateQueries({ queryKey: ['effective-payment-line-item-rates'] });
        },
    });
};
